/**
 * Wettelijke AOW-leeftijd per geboortedatum (bron: SVB / Rijksoverheid).
 *
 * De AOW-leeftijd hangt af van de geboortedatum, NIET van een vast getal. Hij
 * is wettelijk vastgelegd t/m kalenderjaar 2031:
 *   - geboren t/m 31-12-1960:            67 jaar
 *   - geboren 01-01-1961 t/m 30-09-1964: 67 jaar + 3 maanden
 *   - geboren ná 30-09-1964:             nog niet wettelijk vastgelegd
 *
 * Oudere cohorten (ruim vóór 1958) hadden een lagere AOW-leeftijd, maar die
 * zijn allemaal al voorbij hun AOW — voor een "heeft AOW bereikt"-toets volstaat
 * 67 daar prima (de uitkomst is identiek). Voor nog-niet-vastgelegde cohorten
 * (ná 30-09-1964) geeft de functie `fallbackJaren` terug (instelbaar via
 * VerlofBeleid.seniorenAowLeeftijd) zodat we niets gokken.
 *
 * BIJWERKEN zodra de wetgever nieuwe jaren vastlegt (~5 jaar vooruit).
 * Peildatum van deze tabel: juni 2026.
 *
 * Datums zijn UTC-middernacht van de NL-kalenderdag (repo-conventie).
 */

export interface AowLeeftijd {
  jaren: number
  maanden: number
}

/** Wettelijk vastgelegde grenzen (UTC-kalenderdag). */
const GRENS_67_JAAR = Date.UTC(1960, 11, 31) // geboren t/m 31-12-1960 → 67 jaar
const GRENS_67_JAAR_3_MND = Date.UTC(1964, 8, 30) // t/m 30-09-1964 → 67 jaar + 3 maanden

/**
 * AOW-leeftijd (jaren + maanden) voor een geboortedatum. Voor cohorten die de
 * wet nog niet heeft vastgelegd geldt `fallbackJaren` (Decimal-jaren, bv. 67,25
 * = 67 jaar + 3 maanden).
 */
export function aowLeeftijdVoorGeboortedatum(
  geboortedatum: Date,
  fallbackJaren = 67,
): AowLeeftijd {
  const t = Date.UTC(
    geboortedatum.getUTCFullYear(),
    geboortedatum.getUTCMonth(),
    geboortedatum.getUTCDate(),
  )
  if (t <= GRENS_67_JAAR) return { jaren: 67, maanden: 0 }
  if (t <= GRENS_67_JAAR_3_MND) return { jaren: 67, maanden: 3 }
  const jaren = Math.trunc(fallbackJaren)
  const maanden = Math.round((fallbackJaren - jaren) * 12)
  return { jaren, maanden }
}

/** UTC-datum waarop iemand met deze geboortedatum de AOW-leeftijd bereikt. */
export function aowDatum(geboortedatum: Date, fallbackJaren = 67): Date {
  const { jaren, maanden } = aowLeeftijdVoorGeboortedatum(geboortedatum, fallbackJaren)
  return new Date(
    Date.UTC(
      geboortedatum.getUTCFullYear() + jaren,
      geboortedatum.getUTCMonth() + maanden,
      geboortedatum.getUTCDate(),
    ),
  )
}

/** Heeft iemand op `peildatum` de AOW-leeftijd bereikt (peildatum >= AOW-datum)? */
export function heeftAowBereikt(
  geboortedatum: Date,
  peildatum: Date,
  fallbackJaren = 67,
): boolean {
  return peildatum.getTime() >= aowDatum(geboortedatum, fallbackJaren).getTime()
}
